import { ValidationRule } from './types';

type Rule = ValidationRule;

interface RuleResult {
    IsValid: boolean;
    Message?: string;
    HasError?: boolean;
}

const toString = (value: any): string | null => {
    if(value === null || value === undefined){
        return null;
    }
    return value.toString();
}

// Multi-value attributes (Type "list") hold arrays, which Array.prototype.toString would flatten
// to a comma-joined string. `containedin` and `notequal` read a collection's elements through this;
// the regex and length operators deliberately keep comparing the joined string.
const asElements = (value: any): (string | null)[] | null => {
    if(value === null || value === undefined){
        return null;
    }
    if(Array.isArray(value)){
        return value.map(toString);
    }
    return [value.toString()];
}

class RuleRequired {
    execute(value: any, rule: Rule): RuleResult {
        if(value !== null && value !== undefined && value !== ""){
            return { IsValid: true, Message: `Field ${rule.DescriptiveField ?? rule.Field} has value '${value}'.` };
        }
        return this.defaultResult(rule);
    }

    defaultResult(rule: Rule): RuleResult {
        return { IsValid: false, Message: `${rule.DescriptiveField ?? rule.Field} is required${getConditionPart(rule)}.` };
    }

    condition(rule: Rule): string | undefined {
        return `${rule.DescriptiveField ?? rule.Field} is present`;
    }
}

class RuleOptional {
    execute(value: any, rule: Rule): RuleResult {
        if(value !== null && value !== undefined && value !== ""){
            return { IsValid: true, Message: `Field ${rule.DescriptiveField ?? rule.Field} has value '${value}'.` };
        }
        return this.defaultResult(rule);
    }

    defaultResult(rule: Rule): RuleResult {
        return { IsValid: true, Message: `${rule.DescriptiveField ?? rule.Field} is optional${getConditionPart(rule)}.` };
    }

    condition(rule: Rule): string | undefined {
        return `${rule.DescriptiveField ?? rule.Field} is present`;
    }
}

class RuleEqual {
    Invert = false;
    constructor(invert?: boolean){
        this.Invert = invert;
    }
    execute(value: any, rule: Rule): RuleResult {
        var targetValue = (rule.Parameters || {})["value"];
        var strValue = toString(value);
        var elements = asElements(value);
        // Only equal aligns to import: notequal over a list is unmeasured and stays element-wise.
        var elementwise = this.Invert || !Array.isArray(value);
        var matched = elementwise && elements !== null && elements.some(element => element === targetValue);
        if (this.Invert && !matched)
            {
                return { IsValid: true, Message: `${rule.DescriptiveField ?? rule.Field} value '${strValue}' does not equal '${targetValue}'.` };
            }
            else if (!this.Invert && matched)
            {
                return { IsValid: true, Message: `${rule.DescriptiveField ?? rule.Field} value '${strValue}' equals '${targetValue}'.` };
            }
            var partial = this.Invert ? "must not equal" : "must equal";
            return { IsValid: false, Message: `${rule.DescriptiveField ?? rule.Field} value '${strValue}' ${partial} '${targetValue}'${getConditionPart(rule)}.` };
    }
    defaultResult(rule: Rule): RuleResult {
        var targetValue = (rule.Parameters || {})["value"];
        var partial = this.Invert ? "not " : "";
        return { IsValid: false, Message: `${rule.DescriptiveField ?? rule.Field} must ${partial}equal '${targetValue}'${getConditionPart(rule)}.` };
    }
    condition(rule: Rule): string | undefined {
        var targetValue = (rule.Parameters || {})["value"];
        var partial = this.Invert ? "not " : "";
        return `${rule.DescriptiveField ?? rule.Field} is ${partial}'${targetValue}'`;
    }
}

class RuleListContains {
    Invert = false;
    constructor(invert?: boolean){
        this.Invert = invert;
    }
    execute(value: any, rule: Rule): RuleResult {
        var options = this.getOptions(rule);
        var strValue = toString(value);
        var elements = asElements(value);
        var allowsEmpty = options.filter(r => r === '' || r === null).length > 0;
        var permitted = (element: string | null) => options.indexOf(element) > -1 || (allowsEmpty && (element === null || element === ''));
        var contained = elements === null ? allowsEmpty : elements.every(permitted);
        if (contained)
        {
            return { IsValid: true, Message: `${rule.DescriptiveField ?? rule.Field} value '${strValue}' is one of these options: ${options.filter(r => r !== null && r !== '').join(", ")}.` };
        }
        return { IsValid: false, Message: `${rule.DescriptiveField ?? rule.Field} value '${strValue}' must be one of these options: ${options.filter(r => r !== null && r !== '').join(", ")}${getConditionPart(rule)}.` };
    }
    defaultResult(rule: Rule): RuleResult {
        var options = this.getOptions(rule);
        return { IsValid: false, Message: `${rule.DescriptiveField ?? rule.Field} must be one of these options: ${options.filter(r => r !== null && r !== '').join(", ")}${getConditionPart(rule)}.` };
    }
    condition(rule: Rule): string | undefined {
        var options = this.getOptions(rule);
        var partial = this.Invert ? "not " : "";
        return `${rule.DescriptiveField ?? rule.Field} is ${partial}one of ${options.filter(r => r !== null && r !== '').join(", ")}.`;
    }
    getOptions(rule: Rule): string[] {
        var targetValue = (rule.Parameters || {})["value"] || "";
        return this.parseOptions(targetValue);
    }
    parseOptions(str: string): string[] {
        var arr = [];
        var quote = false;  // 'true' means we're inside a quoted field
        if(!str){
            return arr;
        }
        // Iterate over each character
        var currentVal = '';
        for (var c = 0; c < str.length; c++)
        {
            var cc = str[c];
            var nc = c < str.length - 1 ? str[c + 1] : '';        // Current character, next character

            // If the current character is a quotation mark, and we're inside a
            // quoted field, and the next character is also a quotation mark,
            // add a quotation mark to the current column and skip the next character
            if (cc === '"' && quote && nc === '"') { currentVal += cc; ++c; continue; }

            // If it's just one quotation mark, begin/end quoted field
            if (cc === '"') { quote = !quote; continue; }

            // If it's a comma and we're not in a quoted field, move on to the next column
            if (cc === ',' && !quote) { arr.push(currentVal); currentVal = ''; continue; }

            // Otherwise, append the current character to the current column
            currentVal += cc;
        }
        arr.push(currentVal);
        return arr.map(r => r.trim());
    }
}

class RuleRegexMatch {
    execute(value: any, rule: Rule): RuleResult {
        var regex = (rule.Parameters || {})["regex"];
        if (regex === null || regex === undefined || regex === "")
        {
            return { IsValid: true, Message: "Rule does not apply (regular expression not configured).", HasError: true};
        }
        var strValue = toString(value);
        strValue = strValue === null ? '' : strValue;
        if (new RegExp(regex).test(strValue))
        {
            return { IsValid: true, Message: `Field ${rule.DescriptiveField ?? rule.Field} value '${strValue}' matches the regular expression '${regex}'.` };
        }
        return { IsValid: false, Message: `${rule.DescriptiveField ?? rule.Field} value '${strValue}' must match the regular expression '${regex}'${getConditionPart(rule)}.` };
    }
    defaultResult(rule: Rule): RuleResult {
        var regex = (rule.Parameters || {})["regex"];
        if (regex === null || regex === undefined || regex === "")
        {
            return { IsValid: true, Message: "Rule does not apply (regular expression not configured).", HasError: true };
        }
        return { IsValid: false, Message: `${rule.DescriptiveField ?? rule.Field} must match the regular expression '${regex}'${getConditionPart(rule)}.` };
    }
    condition(rule: Rule): string | undefined {
        var regex = (rule.Parameters || {})["regex"];
        if (regex === null || regex === undefined || regex === "")
        {
            return;
        }
        return `${rule.DescriptiveField ?? rule.Field} matches '${regex}'`;
    }
}

class RuleRegexNotMatch {
    execute(value: any, rule: Rule): RuleResult {
        var regex = (rule.Parameters || {})["regex"];
        if (regex === null || regex === undefined || regex === "")
        {
            return { IsValid: true, Message: "Rule does not apply (regular expression not configured).", HasError: true};
        }
        var strValue = toString(value);
        strValue = strValue === null ? '' : strValue;
        if (!new RegExp(regex).test(strValue))
        {
            return { IsValid: true, Message: `Field ${rule.DescriptiveField ?? rule.Field} value '${strValue}' does not match the regular expression '${regex}'.` };
        }
        return { IsValid: false, Message: `${rule.DescriptiveField ?? rule.Field} value '${strValue}' must not match the regular expression '${regex}'${getConditionPart(rule)}.` };
    }
    defaultResult(rule: Rule): RuleResult {
        var regex = (rule.Parameters || {})["regex"];
        if (regex === null || regex === undefined || regex === "")
        {
            return { IsValid: true, Message: "Rule does not apply (regular expression not configured).", HasError: true };
        }
        return { IsValid: false, Message: `${rule.DescriptiveField ?? rule.Field} must not match the regular expression '${regex}'${getConditionPart(rule)}.` };
    }
    condition(rule: Rule): string | undefined {
        var regex = (rule.Parameters || {})["regex"];
        if (regex === null || regex === undefined || regex === "")
        {
            return;
        }
        return `${rule.DescriptiveField ?? rule.Field} does not match '${regex}'`;
    }
}

class RuleLength {
    Comparator: (val: number, limit: number) => boolean;
    ComparatorDescription: string;
    constructor(comparator: (val: number, limit: number) => boolean, comparatorDescription: string){
        this.Comparator = comparator;
        this.ComparatorDescription = comparatorDescription;
    }
    execute(value: any, rule: Rule): RuleResult {
        var targetValue = (rule.Parameters || {})["value"];
        var targetLength = parseInt(targetValue || "0");
        var strValue = toString(value);
        var length = strValue === null ? 0 : strValue.length;
        if (this.Comparator(length, targetLength))
        {
            return { IsValid: true, Message: `${rule.DescriptiveField ?? rule.Field} value '${strValue}' length is ${this.ComparatorDescription} ${targetLength}.` };
        }
        return { IsValid: false, Message: `${rule.DescriptiveField ?? rule.Field} value '${strValue}' length is not ${this.ComparatorDescription} ${targetLength}${getConditionPart(rule)}.` };
    }
    defaultResult(rule: Rule): RuleResult {
        var targetValue = (rule.Parameters || {})["value"];
        return { IsValid: false, Message: `${rule.DescriptiveField ?? rule.Field} must have length ${this.ComparatorDescription} ${targetValue}${getConditionPart(rule)}.` };
    }
    condition(rule: Rule): string | undefined {
        var targetValue = (rule.Parameters || {})["value"];
        return `${rule.DescriptiveField ?? rule.Field} length is ${this.ComparatorDescription} ${targetValue}`;
    }
}

const ruleHandlers: Record<string, any> = {
    "required":  new RuleRequired(),
    "optional":  new RuleOptional(),
    "match": new RuleRegexMatch(),
    "notmatch": new RuleRegexNotMatch(),
    "equal": new RuleEqual(),
    "notequal": new RuleEqual(true),
    "lengthlt": new RuleLength((val, limit) => val < limit, "less than"),
    "lengtheq": new RuleLength((val, limit) => val === limit, "equal to"),
    "containedin": new RuleListContains()
}

const getConditionPart = (rule: Rule): string => {
    var conditions = [];
    if(!rule || !rule.Conditions){
        return "";
    }
    rule.Conditions.forEach(cond => {
        if(!cond || !cond.Type || !ruleHandlers[cond.Type.toLowerCase()]){
            return;
        }
        var message = ruleHandlers[cond.Type.toLowerCase()].condition(cond);
        if(message){
            conditions.push(message);
        }
    });

    if(conditions.length > 0){
        if(conditions.length > 2){
            var first = conditions.slice(0, conditions.length - 1);
            return " when " + first.join(", ") + " and " + conditions[conditions.length - 1];
        }
        return " when " + conditions.join(" and ");
    }
    return "";
}

const getAvailableOptions = (target: any, rule: Rule): string[] => {
    if(!rule || !rule.Type || !ruleHandlers[rule.Type.toLowerCase()]){
        return [];
    }
    var handler = ruleHandlers[rule.Type.toLowerCase()];
    if(!(handler instanceof RuleListContains)){
        return [];
    }
    if(rule.Conditions && !rule.Conditions.every(c => runRule(target, c).IsValid)){
        return [];
    }else{
        return handler.getOptions(rule).filter(r => r !== null && r !== '');
    }
}

const runRule = (target: any, rule: Rule, requireOptional?: boolean): any => {
    if(!rule || !rule.Type || !ruleHandlers[rule.Type.toLowerCase()]){
        return [{ IsValid: true, Message: "Rule does not apply (unknown type).", HasError: true}];
    }
    let handler = ruleHandlers[rule.Type.toLowerCase()];

    if(requireOptional && handler instanceof RuleOptional){
        handler = ruleHandlers["required"];
    }
    let value = target === null || target === undefined ? null : target[rule.Field];
    if(rule.Conditions && !rule.Conditions.every(c => runRule(target, c).IsValid)){
        return { IsValid: true, Message: `Rule does not apply to ${rule.Field} (conditions not met).` };
    }else{
        let result = handler.execute(value, rule);
        if(!result.IsValid && rule.ErrorMessage){
            result.Message = rule.ErrorMessage;
        }
        return result;
    }
}

const getRuleDescription = (rule: Rule): string => {
    if(!rule || !rule.Type || !ruleHandlers[rule.Type.toLowerCase()]){
        return "";
    }
    let defaultMessage = ruleHandlers[rule.Type.toLowerCase()].defaultResult(rule);
    if(!defaultMessage.HasError){
        return rule.ErrorMessage ? rule.ErrorMessage : defaultMessage.Message || "";
    }
    return "";
}

const nonRuleAttributes = ["tags", "attribute_set", "approval_status", "sync_status", "image_metadata"];

// Returns null - the caller must fail closed - for every case the Save form also refuses: no set
// named, an unknown set, or a set resolving to no attributes.
const scopeAttributesToSet = (attributes: any[], attributeSets: any[], attributeSetName: any): any[] | null => {
    if(!attributeSetName){
        return null;
    }
    const usedSet = (attributeSets || []).find(set => set && set.Name === attributeSetName);
    if(!usedSet || !Array.isArray(usedSet.Attributes)){
        return null;
    }
    const scoped = (attributes || []).filter(attribute => attribute && usedSet.Attributes.indexOf(attribute.Name) > -1);
    return scoped.length > 0 ? scoped : null;
}

const runAttributeRules = (attributes: any[], target: any): Record<string, string> => {
    const errors: Record<string, string> = {};
    (attributes || []).forEach(attribute => {
        if(!attribute || !attribute.Name || !attribute.ValidationRules){
            return;
        }
        if(nonRuleAttributes.indexOf(attribute.Name) > -1){
            return;
        }
        attribute.ValidationRules.forEach((rule: Rule) => {
            const result = runRule(target, { ...rule, Field: attribute.Name, DescriptiveField: attribute.FriendlyName });
            if(result.IsValid === false){
                errors[attribute.Name] = attribute.Name + " " + result.Message;
            }
        });
    });
    return errors;
}

export { runRule as RunRule, getRuleDescription as GetRuleDescription, getAvailableOptions as GetAvailableOptions, runAttributeRules as RunAttributeRules, scopeAttributesToSet as ScopeAttributesToSet };
